/**
 * In-memory cache for data fetching promises
 * Helps prevent duplicate requests during concurrent rendering
 */
const __sharedDataCache = new Map<string, Promise<any>>();

// Results cache with TTL (5 minutes)
const __resultsCache = new Map<
  string,
  {
    data: any;
    timestamp: number;
  }
>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches data once and returns the same Promise if called again
 * with the same cacheKey (i.e. the same parameters).
 * Caches results for a short period to reduce requests across page loads.
 */
export function getSharedData<T>(
  fetchFn: () => Promise<T>,
  cacheKey: string,
  options?: { bypassCache?: boolean; ttl?: number; revalidate?: number }
): Promise<T> {
  const ttl = options?.ttl || CACHE_TTL;

  // Check results cache first (unless bypassCache is true)
  if (!options?.bypassCache) {
    const cachedResult = __resultsCache.get(cacheKey);
    if (cachedResult && Date.now() - cachedResult.timestamp < ttl) {
      return Promise.resolve(cachedResult.data);
    }
  }

  // Check for in-flight requests
  if (__sharedDataCache.has(cacheKey)) {
    return __sharedDataCache.get(cacheKey)!;
  }

  // Otherwise, call the fetch function and cache both the promise and its result
  const promise = fetchFn().then((result) => {
    // Store the result in the results cache
    __resultsCache.set(cacheKey, {
      data: result,
      timestamp: Date.now(),
    });
    // Clear from the in-flight cache
    __sharedDataCache.delete(cacheKey);
    return result;
  });

  __sharedDataCache.set(cacheKey, promise);
  return promise;
}
